import moment from 'moment';

/**
 * Общие хелперы для выгрузки в Excel через react-data-export (режим dataSet).
 *
 * Стили — ARGB-эквиваленты токенов из `src/scss/_colors.scss`:
 *   $gray-200 (#F0F0F4) — заливка заголовка,
 *   $gray-300 (#DEE2E6) — рамка.
 * Менять цвет — здесь, а не в каждом отчёте.
 *
 * См. эталон использования: src/modules/orders/EvacuatorOrders.tsx.
 */

// --- Стили ---

export const EXCEL_HEADER_STYLE = {
  font: { bold: true },
  fill: { patternType: 'solid', fgColor: { rgb: 'FFF0F0F4' } },
  border: {
    top: { style: 'thin', color: { rgb: 'FFDEE2E6' } },
    bottom: { style: 'thin', color: { rgb: 'FFDEE2E6' } },
    left: { style: 'thin', color: { rgb: 'FFDEE2E6' } },
    right: { style: 'thin', color: { rgb: 'FFDEE2E6' } },
  },
} as const;

export const EXCEL_CENTER_STYLE = { alignment: { horizontal: 'center' as const, vertical: 'center' as const } };
export const EXCEL_RIGHT_STYLE = { alignment: { horizontal: 'right' as const, vertical: 'center' as const } };

// --- Форматтеры ---

export const formatExcelMoney = (v?: number) =>
  (v != null && v !== 0) ? v.toLocaleString(navigator.language, {
    minimumFractionDigits: 2,
    useGrouping: false, 
  }) : '';

export type ExcelDateTz = 'local' | 'Europe/Moscow' | 'Etc/UTC';

/** Unix-timestamp (секунды) → строка. По умолчанию local, формат 'DD.MM.yyyy HH:mm'. */
export const formatExcelDate = (
  ts?: number,
  opts?: { tz?: ExcelDateTz; format?: string }
): string => {
  if (!ts) return '';
  const { tz = 'local', format = 'DD.MM.yyyy HH:mm' } = opts ?? {};
  const m = moment.unix(ts);
  return (tz === 'local' ? m.local() : m.tz(tz)).format(format);
};

// --- Фабрика dataSet ---

export interface ExcelExportColumn {
  title: string;
  name: string;
  width?: number;
  align?: 'center';
}

export interface ExcelDataSet {
  columns: Array<{ title: string; width: { wpx: number }; style: object }>;
  data: Array<Array<{ value: string; style?: object }>>;
}

/**
 * Строит dataSet для <ExcelSheet dataSet={...}/> из описания колонок и строк.
 *
 * Поведение значения ячейки:
 *  - `getCellValue` задан → используется (своя логика отчёта);
 *  - иначе, если колонка в `moneyColumns` → formatExcelMoney;
 *  - иначе — raw `row[name] ?? ''`.
 *
 * Поведение стиля ячейки:
 *  - колонка в `moneyColumns` → RIGHT;
 *  - иначе колонка в `centerColumns` или `col.align === 'center'` → CENTER;
 *  - иначе — без стиля.
 */
export const buildExcelDataSet = <Row>(opts: {
  columns: ExcelExportColumn[];
  rows: Row[];
  moneyColumns?: Set<string>;
  centerColumns?: Set<string>;
  getCellValue?: (name: string, row: Row) => string;
}): ExcelDataSet[] => {
  const {
    columns, rows, moneyColumns, centerColumns, getCellValue, 
  } = opts;

  const defaultGetValue = (name: string, row: Row): string =>
    moneyColumns?.has(name) ? formatExcelMoney((row as any)[name]) : ((row as any)[name] ?? '');

  return [{
    columns: columns.map((col) => ({
      title: col.title,
      width: { wpx: col.width ?? 120 },
      style: { ...EXCEL_HEADER_STYLE, ...(col.align === 'center' ? EXCEL_CENTER_STYLE : {}) },
    })),
    data: rows.map((row) => columns.map((col) => ({
      value: getCellValue ? getCellValue(col.name, row) : defaultGetValue(col.name, row),
      style: moneyColumns?.has(col.name)
        ? EXCEL_RIGHT_STYLE
        : ((centerColumns?.has(col.name) || col.align === 'center') ? EXCEL_CENTER_STYLE : undefined),
    }))),
  }];
};
